import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { StaffDailyLineChart } from "../../admin-shared/analytics/AnalyticsChartKit";
import Loader from "../components/Loader";
import { fetchPartnerStaffAnalytics } from "../utils/partnerStaffAnalyticsApi";
import {
  currentMonthPickerValue,
  downloadStaffReportCsv,
  monthRangeFromPicker,
} from "../utils/staffReportCsv";
import "./PartnerCheckinAnalytics.css";
import { EmberAnalyticsShell } from "../../admin-shared/analytics/EmberAnalyticsShell";
import { daysAgoIso, todayIso } from "../../admin-shared/analytics/analyticsDateUtils";
import HqFranchiseFilterField from "../../admin-shared/analytics/HqFranchiseFilterField";
import { staffRoleLabel } from "../../admin-shared/staffRoleOptions";

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");
const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const MetricCard = ({ label, value, hint }) => (
  <article className="pca-metric">
    <p className="pca-metric__label">{label}</p>
    <p className="pca-metric__value">{value}</p>
    {hint ? <p className="pca-metric__hint">{hint}</p> : null}
  </article>
);

export default function PartnerStaffAnalytics({
  portalBase = "/nyafaz-ep/partner",
  portalLabel = "Franchise partner",
  fetchStaffAnalytics = fetchPartnerStaffAnalytics,
  hqApi = null,
  reportMode = false,
}) {
  const [franchiseId, setFranchiseId] = useState("");
  const [monthPicker, setMonthPicker] = useState(() => currentMonthPickerValue());
  const initialReportRange = reportMode ? monthRangeFromPicker(currentMonthPickerValue()) : null;
  const [fromDate, setFromDate] = useState(
    () => initialReportRange?.fromDate || daysAgoIso(29),
  );
  const [toDate, setToDate] = useState(
    () => initialReportRange?.toDate || todayIso(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchStaffAnalytics({
        fromDate,
        toDate,
        franchiseId: franchiseId || undefined,
      });
      setAnalytics(data);
    } catch (err) {
      setError(err?.message || "Failed to load staff analytics");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, franchiseId, fetchStaffAnalytics]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!reportMode) return;
    const range = monthRangeFromPicker(monthPicker);
    if (range) {
      setFromDate(range.fromDate);
      setToDate(range.toDate);
    }
  }, [monthPicker, reportMode]);

  const summary = analytics?.summary || {};
  const rangeLabel = reportMode && monthPicker ? monthPicker : `${fromDate} – ${toDate}`;
  const staff = useMemo(
    () => (Array.isArray(analytics?.staff) ? analytics.staff : []),
    [analytics],
  );
  const daily = useMemo(
    () => (Array.isArray(analytics?.daily) ? analytics.daily : []),
    [analytics],
  );

  if (loading && !analytics) return <Loader />;

  const filters = (
    <div className="pca-filters">
      {hqApi ? (
        <HqFranchiseFilterField api={hqApi} value={franchiseId} onChange={setFranchiseId} />
      ) : null}
      {reportMode ? (
        <label className="pca-filters__field">
          Month
          <input
            type="month"
            value={monthPicker}
            onChange={(e) => setMonthPicker(e.target.value)}
          />
        </label>
      ) : (
        <>
          <label className="pca-filters__field">
            From
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </label>
          <label className="pca-filters__field">
            To
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </label>
        </>
      )}
      <button type="button" className="pca-btn" onClick={load} disabled={loading}>
        {loading ? "Loading…" : "Apply"}
      </button>
    </div>
  );

  const actions = (
    <>
      {reportMode ? (
        <button
          type="button"
          className="pca-btn"
          disabled={!analytics || loading}
          onClick={() => downloadStaffReportCsv(analytics, rangeLabel)}
        >
          Export CSV
        </button>
      ) : null}
      {portalBase.includes("/partner") && !reportMode ? (
        <Link to={`${portalBase}/staff`} className="pca-btn pca-btn--ghost">
          Manage staff
        </Link>
      ) : null}
      {portalBase.includes("/partner") && reportMode ? (
        <Link to={`${portalBase}/staff-availability`} className="pca-btn pca-btn--ghost">
          Schedule & leave
        </Link>
      ) : null}
      <Link to={`${portalBase}/overview`} className="pca-btn pca-btn--ghost">
        ← Overview
      </Link>
    </>
  );

  return (
    <EmberAnalyticsShell
      eyebrow={portalLabel}
      title={reportMode ? "Staff report" : "Staff analytics"}
      lede={
        reportMode
          ? "Monthly outlet performance per team member (check-ins, services, flows). For booking slots and time off, use Schedule & leave — not included in this export."
          : "Per-stylist check-ins, services taken, and flow activity."
      }
      actions={actions}
      filters={filters}
      error={error}
    >
      <div className="pca-metrics pca-metrics--primary">
        <MetricCard
          label="Active staff"
          value={formatNumber(summary.staffActive)}
          hint={`${formatNumber(summary.staffTotal)} on roster · ${formatNumber(summary.staffWithActivity)} with activity`}
        />
        <MetricCard
          label="Check-ins completed"
          value={formatNumber(summary.checkinsCompleted)}
          hint="Visits finished by staff"
        />
        <MetricCard
          label="Service lines taken"
          value={formatNumber(summary.serviceLinesTaken)}
          hint={`Sales ₹ ${formatMoney(summary.sales)}`}
        />
        <MetricCard
          label="Flow events"
          value={formatNumber(summary.flowEvents)}
          hint={`${formatNumber(summary.uniqueGuests)} unique guests`}
        />
      </div>

      <section className="pca-panel pca-panel--chart">
        <h2 className="pca-panel__title">Daily staff activity</h2>
        {daily.length ? (
          <StaffDailyLineChart data={daily} />
        ) : (
          <p className="pca-panel__empty">No staff activity in this range.</p>
        )}
      </section>

      <section className="pca-panel pca-panel--full">
        <h2 className="pca-panel__title">Staff performance</h2>
        {staff.length ? (
          <div className="pca-table-wrap">
            <table className="pca-table ember-analytics__table">
              <thead>
                <tr>
                  <th>Staff</th>
                  {reportMode ? <th>Mobile</th> : null}
                  <th>Check-ins</th>
                  <th>Done</th>
                  <th>Services</th>
                  <th>Svc done</th>
                  <th>Sales</th>
                  <th>Flows</th>
                  <th>Guests</th>
                  <th>Skin</th>
                  <th>Hair</th>
                  <th>Style</th>
                  <th>Color</th>
                  <th>Top services</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((row) => (
                  <tr key={row.stylistId || row.stylistName}>
                    <td>
                      {row.stylistName}
                      {row.role ? (
                        <span style={{ opacity: 0.55, marginLeft: 6, fontSize: 11 }}>
                          {staffRoleLabel(row.role)}
                        </span>
                      ) : null}
                    </td>
                    {reportMode ? <td>{row.mobile || "—"}</td> : null}
                    <td>{formatNumber(row.checkinsQueued)}</td>
                    <td>{formatNumber(row.checkinsCompleted)}</td>
                    <td>{formatNumber(row.serviceLinesTaken)}</td>
                    <td>{formatNumber(row.serviceLinesCompleted)}</td>
                    <td>₹ {formatMoney(row.serviceSales)}</td>
                    <td>{formatNumber(row.flowTotal)}</td>
                    <td>{formatNumber(row.uniqueGuests)}</td>
                    <td>{formatNumber(row.byFlow?.skin_analysis)}</td>
                    <td>{formatNumber(row.byFlow?.hair_analysis)}</td>
                    <td>{formatNumber(row.byFlow?.hairstyle)}</td>
                    <td>{formatNumber(row.byFlow?.hair_coloring)}</td>
                    <td>
                      {(row.topServices || [])
                        .map((s) => `${s.name} (${s.count})`)
                        .join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="pca-panel__empty">No staff records or activity in this range.</p>
        )}
      </section>
    </EmberAnalyticsShell>
  );
}
